import * as React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  SEGMENT_FIELD_REGISTRY,
  segmentFieldEnum,
  type SegmentGroup,
  type SegmentCondition,
  type SegmentLogicalOp,
} from '@masters-club/validation';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { SegmentConditionEditor } from './SegmentConditionEditor';
import { logicalOpLabel } from './segment-labels';

interface SegmentGroupEditorProps {
  group: SegmentGroup;
  groupIndex: number;
  onChange: (next: SegmentGroup) => void;
  onRemove: () => void;
  disabled?: boolean;
}

function createDefaultCondition(): SegmentCondition {
  const firstField = segmentFieldEnum.options[0];
  const meta = SEGMENT_FIELD_REGISTRY[firstField];
  return {
    field: firstField,
    operator: meta.operators[0],
    value: undefined,
  };
}

export function SegmentGroupEditor({
  group,
  groupIndex,
  onChange,
  onRemove,
  disabled = false,
}: SegmentGroupEditorProps) {
  const handleConditionsOpChange = (op: SegmentLogicalOp) => {
    onChange({ ...group, conditionsOp: op });
  };

  const handleConditionChange = (index: number, next: SegmentCondition) => {
    const conditions = group.conditions.map((c, i) => (i === index ? next : c));
    onChange({ ...group, conditions });
  };

  const handleConditionRemove = (index: number) => {
    const conditions = group.conditions.filter((_, i) => i !== index);
    onChange({ ...group, conditions });
  };

  const handleAddCondition = () => {
    onChange({
      ...group,
      conditions: [...group.conditions, createDefaultCondition()],
    });
  };

  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
      {/* Заголовок группы */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          Группа {groupIndex + 1}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={disabled}
          className="text-muted-foreground hover:text-destructive h-7 px-2"
          aria-label="Удалить группу"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Переключатель AND/OR внутри группы */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Условия объединены через:</span>
        <div className="flex rounded-md border overflow-hidden text-xs">
          {(['AND', 'OR'] as SegmentLogicalOp[]).map((op) => (
            <button
              key={op}
              type="button"
              disabled={disabled}
              onClick={() => handleConditionsOpChange(op)}
              className={`px-3 py-1 transition-colors ${
                group.conditionsOp === op
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              {logicalOpLabel(op)}
            </button>
          ))}
        </div>
      </div>

      <Separator />

      {/* Список условий */}
      <div className="space-y-2">
        {group.conditions.map((condition, i) => (
          <React.Fragment key={i}>
            {i > 0 && (
              <div className="flex items-center gap-2 py-1">
                <Separator className="flex-1" />
                <span className="text-xs font-medium text-muted-foreground px-2">
                  {logicalOpLabel(group.conditionsOp)}
                </span>
                <Separator className="flex-1" />
              </div>
            )}
            <SegmentConditionEditor
              condition={condition}
              onChange={(next) => handleConditionChange(i, next)}
              onRemove={() => handleConditionRemove(i)}
              disabled={disabled}
            />
          </React.Fragment>
        ))}
      </div>

      {/* Кнопка добавить условие */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleAddCondition}
        disabled={disabled || group.conditions.length >= 50}
        className="text-muted-foreground hover:text-foreground w-full border border-dashed"
      >
        <Plus className="h-4 w-4 mr-1" />
        Добавить условие
      </Button>
    </div>
  );
}
